import {ApolloError, useQuery} from "@apollo/client";

import useAuth from "shared/Auth/hooks/useAuth";
import {GetUserByIdsQuery} from "shared/Inbox/queries/GetUserByIds";
import {UserField, ChatConversation} from "src/types/generated";

import {useUniversity} from "../../../widgets/modules/UniversityProvider/UniversityProvider";

type ConversationWithOpponentId = ChatConversation & {
    opponentId: string;
};

type ConversationsWithOpponent = ChatConversation & {
    opponent: Partial<UserField>;
};
type UseConversationsWithOpponentsResult = {
    loading: boolean;
    error: ApolloError;
    conversations: ConversationsWithOpponent[];
};

export function useConversationsWithOpponents(
    conversations: ChatConversation[],
): UseConversationsWithOpponentsResult {
    const authState = useAuth();
    const {squareLogo} = useUniversity();
    const userId = authState?.user?.id;

    const conversationsWithOpponentId: ConversationWithOpponentId[] = conversations.map(
        (conversation: ChatConversation) => {
            const members = conversation.members || [];
            let opponentId = null;

            if (members.length === 2) {
                opponentId = members.find((member: UserField) => member.id !== userId)?.id;
            } else if (members.length > 2) {
                opponentId = members.find((member: UserField) =>
                    ["mentor", "staff"].includes(member.accountRole),
                )?.id;
            }

            return {
                ...conversation,
                opponentId,
            };
        },
    );

    const opponentIds: string[] = conversationsWithOpponentId.map(
        (conversation) => conversation.opponentId,
    );

    const {
        loading,
        data: opponentData,
        error,
    } = useQuery<{getUserByIds: UserField[]}>(GetUserByIdsQuery, {
        variables: {
            idIn: opponentIds,
        },
    });

    const conversationsWithOpponents: ConversationsWithOpponent[] = loading
        ? []
        : conversationsWithOpponentId.map((conversation: ConversationWithOpponentId) => {
              const opponentDataMatch = opponentData?.getUserByIds?.find(
                  (opponent) => opponent.id === conversation.opponentId,
              );
              if (opponentDataMatch?.accountRole === "bot") {
                  return {
                      ...conversation,
                      opponent: {
                          firstName: opponentDataMatch?.firstName,
                          profilePhoto: squareLogo,
                          accountRole: "university",
                          inboxInfoOne: "",
                      },
                  };
              }
              return {
                  ...conversation,
                  opponent: {
                      firstName: opponentDataMatch?.firstName,
                      profilePhoto: opponentDataMatch?.profilePhoto,
                      accountRole: opponentDataMatch?.accountRole,
                      countryCode: opponentDataMatch?.country?.code,
                      isOnline: opponentDataMatch?.isOnline,
                      degrees: opponentDataMatch?.degrees,
                      inboxInfoOne:
                          opponentDataMatch.accountRole === "staff"
                              ? opponentDataMatch?.staffFields?.department ?? ""
                              : opponentDataMatch?.accountRole === "mentor"
                              ? opponentDataMatch?.degrees?.[0]?.degree?.name ?? ""
                              : "",
                  },
              };
          });

    return {loading, error, conversations: conversationsWithOpponents};
}
